import { AgentSearchService } from 'ag-common-svc/lib/services/agent-search';
import { Component, HostBinding, inject, Input, ViewChild } from '@angular/core';
import { APPROVE_DENY_REASON_VISIBILITY_LEVEL_LOOKUP, Agent, AgentKeys, ApproveDenyReason, ApproveDenyReasonKeys, ApproveDenyReasonVisibilityLevel, ChangeSourceType, Lookup, LookupKeys } from '@ag-common-lib/public-api';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { AgentApproveDenyReasonsService } from '../../../../services/agent-approve-deny-reason.service';
import { BehaviorSubject, EMPTY, Observable, combineLatest, from } from 'rxjs';
import { catchError, filter, map, mergeMap, shareReplay, switchMap } from 'rxjs/operators';
import { ModalWindowComponent } from '../../../modal-window/modal-window.component';
import { QueryParam, WhereFilterOperandKeys } from '../../../../dao/CommonFireStoreDao.dao';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { ChangeSourceTypeService } from 'ag-common-svc/lib/services/change-source-type.service';

@Component({
  selector: 'ag-shr-approve-deny-reasons',
  templateUrl: './approve-deny-reasons.component.html',
  styleUrls: ['./approve-deny-reasons.component.scss'],
})
export class ApproveDenyReasonsComponent {
  @HostBinding('class') className = 'approve-deny-reasons';
  @Input() set agent(agent: Agent) {
    this.agentDbId$.next(agent[BaseModelKeys.dbId]);
  }
  @Input() readOnly = false;
  @Input() canEdit: boolean = true;
  @Input() canDelete: boolean = true;
  @Input() canCreate: boolean = true;
  @Input() set allowedVisibilityLevels(data: ApproveDenyReasonVisibilityLevel[]) {
    if (Array.isArray(data)) {
      this.allowedVisibilityLevels$.next(data);
    }
  }
  @ViewChild('approveDenyReasonModalRef', { static: true }) approveDenyReasonModalComponent: ModalWindowComponent;

  private changeSourceTypeService = inject(ChangeSourceTypeService);

  sourceType: ChangeSourceType = this.changeSourceTypeService.sourceType();

  public inProgress$: Observable<boolean>;
  public agentsDataSource$: Observable<any>;
  public BaseModelKeys = BaseModelKeys;
  public ApproveDenyReasonKeys = ApproveDenyReasonKeys;
  public approveDenyReasonVisibilityLevelLookup = APPROVE_DENY_REASON_VISIBILITY_LEVEL_LOOKUP;
  public approveDenyReasonFormData: ApproveDenyReason;
  public approveDenyReasons$: Observable<DataSource>;
  public readonly agentDbId$ = new BehaviorSubject<string>(undefined);

  private allowedVisibilityLevels$ = new BehaviorSubject<ApproveDenyReasonVisibilityLevel[]>([]);

  constructor(
    private agentSearchService: AgentSearchService,
    private agentApproveDenyReasonsService: AgentApproveDenyReasonsService,
  ) {
    this.approveDenyReasons$ = combineLatest([this.agentDbId$, this.allowedVisibilityLevels$]).pipe(
      filter(Boolean),
      mergeMap(([agentDbId, allowedVisibilityLevels]) => {
        const qp: QueryParam[] = [];

        if (Array.isArray(allowedVisibilityLevels) && allowedVisibilityLevels?.length) {
          const visibilityLevelsQueryParam = new QueryParam(
            ApproveDenyReasonKeys.visibilityLevel,
            WhereFilterOperandKeys.in,
            allowedVisibilityLevels,
          );
          qp.push(visibilityLevelsQueryParam);
        }

        return this.agentApproveDenyReasonsService.getList(agentDbId, qp);
      }),

      map(approveDenyReasons => {
        return new DataSource({
          store: new ArrayStore({
            key: 'dbId',
            data: Array.isArray(approveDenyReasons) ? approveDenyReasons : [],
          }),
        });
      }),
      shareReplay(1),
    );

    this.agentsDataSource$ = from(this.agentSearchService.search({ sort: [{ selector: AgentKeys.p_agent_first_name, desc: false }] })).pipe(
      map(response => response?.data ?? []),
      map((response): Partial<Lookup>[] =>
        Array.isArray(response)
          ? response.map((agent: Agent) => {
              const description = [agent?.p_agent_first_name, agent?.p_agent_last_name].filter(Boolean).join(' ');
              const value = agent?.p_email;

              return {
                key: agent?.dbId,
                [LookupKeys.value]: value,
                [LookupKeys.description]: description,
              };
            })
          : [],
      ),
    );
  }

  public showListPopup = async () => {
    this.approveDenyReasonModalComponent.showModal();
  };

  public onRowRemoving = e => {
    e.cancel = this.agentApproveDenyReasonsService.delete(
      this.agentDbId$.value,
      e.data[BaseModelKeys.dbId],
      this.sourceType,
    );
  };

}
